/** @jsx jsx */
import type { Interpolation } from '@emotion/react';
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Dropdown, MenuList } from '@coursera/cds-core';
import type { IconProps, Theme } from '@coursera/cds-core';

import { MenuContext } from './MenuContext';

const styles = {
  inlineContainer: css`
    display: inline-block;
  `,
  button: css`
    border-radius: 0;
    border-bottom: 2px solid transparent;
    margin-bottom: var(--cds-spacing-100);
    padding-left: var(--cds-spacing-100);
    padding-right: var(--cds-spacing-100);
    margin-left: calc(var(--cds-spacing-100) * -1);

    &:hover,
    &:focus,
    &[aria-expanded='true'] {
      border-bottom: 2px solid var(--cds-color-interactive-primary-hover);
      text-decoration: none;
    }
  `,
  menuRoot: css`
    padding: var(--cds-spacing-200);
    pointer-events: auto;
    overflow: auto;
    flex: 1;
  `,
};

export type ActionMenuProps = {
  buttonLabel: string | React.ReactNode;
  /**
   * Can only be "small" or "medium". default is "medium"
   */
  buttonSize?: 'small' | 'medium';
  buttonDisabled?: boolean;
  id: string;
  /**
   * Menu contents, normally `ActionMenuItem` or `ActionItemsGroup.
   */
  children?: React.ReactNode;
  onOpenIcon?: React.ReactElement<IconProps>;
  onCloseIcon?: React.ReactElement<IconProps>; // default to openIcon when not provided
  /**
   * Can only be "before" or "after". default is "after"
   */
  iconPosition?: 'before' | 'after';
  /**
   * Will use width to match contents rather than forcing a min width. Default is false
   */
  fitContentWidth?: boolean;
  /**
   * Origin point preferences of the menu
   */
  originVertical?: 'top' | 'bottom';
  originHorizontal?: 'left' | 'right';
  /**
   * Style overrides
   */
  cssOverride?: Interpolation<Theme>;
  inheritStyle?: boolean;
  inlineContainer?: boolean;
};

const ActionMenu = (props: ActionMenuProps) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const {
    buttonLabel,
    children,
    id,
    onOpenIcon,
    onCloseIcon = onOpenIcon,
    iconPosition = 'after',
    inheritStyle = false,
    buttonSize = 'medium',
    buttonDisabled = false,
    fitContentWidth = false,
    originVertical = 'top',
    originHorizontal = 'left',
    cssOverride = {},
    inlineContainer,
  } = props;

  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const contextValue = {
    onClose: handleClose,
  };

  return (
    <div css={inlineContainer && styles.inlineContainer}>
      <Button
        id={`${id}-button`}
        aria-controls={open ? id : undefined}
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : 'false'}
        css={!inheritStyle && styles.button}
        onClick={handleOpen}
        type="button"
        variant="ghost"
        icon={open ? onOpenIcon : onCloseIcon}
        iconPosition={iconPosition}
        size={buttonSize}
        disabled={buttonDisabled}
      >
        {buttonLabel}
      </Button>

      <MenuContext.Provider value={contextValue}>
        <Dropdown
          anchorElement={anchorEl}
          open={open}
          css={cssOverride}
          onClose={handleClose}
          id={id}
          anchorOrigin={{
            vertical: originVertical === 'top' ? 'bottom' : 'top',
            horizontal: originHorizontal,
          }}
          transformOrigin={{
            vertical: originVertical,
            horizontal: originHorizontal,
          }}
          PaperProps={{
            style: {
              ...(!fitContentWidth && { minWidth: '360px' }),
            },
          }}
          maxHeight="60%"
        >
          <div css={styles.menuRoot}>
            <MenuList aria-labelledby={`${id}-button`}>{children}</MenuList>
          </div>
        </Dropdown>
      </MenuContext.Provider>
    </div>
  );
};

export default ActionMenu;
